// Inline "Download the app" CTA used to cap web-only lists/rails/feeds.
// Comes in four shapes that match neighbouring cards.
// Tapping opens the in-app "how to download" walkthrough rather than the
// Play Store directly — we want organic Play Store searches.
import { useState } from "react";
import { Smartphone } from "lucide-react";
import { cn } from "@/lib/utils";
import { HowToDownloadDialog } from "@/components/HowToDownloadDialog";

interface Props {
  variant?: "rail-tile" | "strip-tile" | "feed-slide" | "row";
  className?: string;
  message?: string;
}

const DEFAULT_MSG = "Download FitAuraAI - Try On Clothes from Google Play Store for more.";

export const GetTheAppInlineCard = ({ variant = "rail-tile", className, message = DEFAULT_MSG }: Props) => {
  const [howOpen, setHowOpen] = useState(false);
  const open = () => setHowOpen(true);

  const tileContent = (
    <>
      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/20 text-primary ring-1 ring-primary/30">
        <Smartphone className="h-5 w-5" />
      </div>
      <p className="mt-2 text-[11px] font-bold leading-tight text-foreground">Get the app</p>
      <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
        Search <span className="font-semibold text-foreground">FitAuraAI - Try On Clothes</span> on Google Play.
      </p>
      <img src="/google-play-badge.svg" alt="Get it on Google Play" className="mt-2 h-8 w-auto" />
    </>
  );

  let node: JSX.Element;

  if (variant === "feed-slide") {
    node = (
      <button
        type="button"
        onClick={open}
        className={cn(
          "relative flex h-full w-full snap-start snap-always flex-col items-center justify-center gap-3 bg-gradient-to-br from-primary/25 via-background to-accent/20 px-6 text-center",
          className,
        )}
      >
        <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br from-primary/30 to-accent/20 text-primary ring-1 ring-primary/30">
          <Smartphone className="h-8 w-8" />
        </div>
        <div>
          <p className="text-lg font-bold">Continue in the app</p>
          <p className="mt-1 max-w-xs text-xs text-muted-foreground">{message}</p>
        </div>
        <img src="/google-play-badge.svg" alt="Get it on Google Play" className="h-12 w-auto" />
      </button>
    );
  } else if (variant === "row") {
    node = (
      <button
        type="button"
        onClick={open}
        className={cn(
          "flex w-full items-center gap-3 rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/10 to-accent/10 p-3 transition active:scale-[0.98]",
          className,
        )}
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/20 text-primary">
          <Smartphone className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1 text-left">
          <p className="text-sm font-bold leading-tight">Get the app for more</p>
          <p className="text-[11px] text-muted-foreground">{message}</p>
        </div>
        <img src="/google-play-badge.svg" alt="Get it on Google Play" className="h-8 w-auto" />
      </button>
    );
  } else {
    // strip-tile (124×166) for FeaturedStrip; rail-tile (124×180) for RailTile
    const isStrip = variant === "strip-tile";
    node = (
      <button
        type="button"
        onClick={open}
        className={cn(
          "relative flex shrink-0 flex-col items-center justify-center overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/15 via-background to-accent/10 p-3 text-center ring-1 ring-white/10 transition active:scale-95",
          isStrip ? "h-[166px] w-[124px]" : "h-[180px] w-[124px]",
          className,
        )}
      >
        {tileContent}
      </button>
    );
  }

  return (
    <>
      {node}
      <HowToDownloadDialog open={howOpen} onOpenChange={setHowOpen} />
    </>
  );
};
